import { ia } from 'lib/helpers/utility';
import extractDataFromColDefs from 'lib/helpers/pdf/tableExport/lib/extractDataFromColDefs';

import {
  generateColumnDefs,
  getColumnMeasureKinds,
  isAdditiveField
} from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';

const omitFields = (row, fields) =>
  Object.fromEntries(Object.entries(row).filter(([field]) => !fields.includes(field)));

// Function to flatten nested children data for CSV/Excel export
// mode: 'summary' = parent rows only, 'detailed' = child rows (optionally with parent context)
export const flattenNestedData = (
  rows,
  columns,
  { mode = 'summary', parentColumns = [], omitRepeatedMeasures = false } = {}
) => {
  const isDetailed = mode === 'detailed';

  if (!isDetailed) {
    // Summary mode: just format and return parent-level rows (no children)
    return extractDataFromColDefs(rows, columns);
  }

  // Detailed mode: return child rows; prepend parent context when parentColumns is provided
  const hasParentContext = ia(parentColumns);
  // A parent amount or count is one figure for the whole group: repeating it on every child row
  // makes a totalled column sum to a multiple of the real total, so exports with totals write it on
  // the group's first row only. Averages and rates are never totalled, so every row keeps them, and
  // without totals every row keeps its full parent context.
  const parentMeasureFields =
    hasParentContext && omitRepeatedMeasures
      ? [...getColumnMeasureKinds(rows).keys()]
          .filter(isAdditiveField)
          .map((field) => `parent_${field}`)
      : [];
  let result = [];

  rows.forEach((row) => {
    const children = row.children;

    if (ia(children)) {
      const childCols = ia(columns) ? columns : generateColumnDefs(children);
      const formattedChildren = extractDataFromColDefs(children, childCols);

      if (hasParentContext) {
        const formattedParent = extractDataFromColDefs([row], parentColumns)[0];
        const namespacedParent = Object.fromEntries(
          Object.entries(formattedParent).map(([k, v]) => [`parent_${k}`, v])
        );
        const parentContext = omitFields(namespacedParent, parentMeasureFields);
        const merged = formattedChildren.map((child, index) => ({
          ...(index === 0 ? namespacedParent : parentContext),
          ...child
        }));
        result = result.concat(merged);
      } else {
        result = result.concat(formattedChildren);
      }
    }
  });

  return result;
};
